import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BUILD_HASH, BUILD_TIMESTAMP } from '../../version';
import { useAppVersionCheck } from '../../hooks/useAppVersionCheck';
import { onClientOutdated } from '../../utils/clientBuild';
import {
  NORMAL_TOAST_MS,
  REQUIRED_COUNTDOWN_S,
  RemoteVersion,
  Urgency,
  applyGrace,
  availableSinceOf,
  deriveUrgency,
  isSnoozed,
  markToastShown,
  readSnooze,
  reloadApp,
  remoteBuildOf,
  snoozeDuration,
  takeRestoreState,
  toastAlreadyShown,
  writeSnooze,
  GRACE_MS,
} from './updateModel';
import {
  discardAllUnsaved,
  hasUnsavedChanges,
  saveAllUnsaved,
  useHasUnsavedChanges,
} from './unsavedChanges';

/** Our own overlays carry this so they never count as "another dialog is open". */
export const APP_UPDATE_ATTR = 'data-app-update';

const FOREIGN_MODAL = `.MuiModal-root:not([${APP_UPDATE_ATTR}]):not(.MuiModal-hidden)`;

/** True while a dialog, drawer or menu other than ours is open. */
function useForeignModalOpen(): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const read = () => setOpen(document.querySelector(FOREIGN_MODAL) !== null);
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return open;
}

export interface UpdateContextValue {
  /** What the person should see now: grace and server backstop already applied. */
  urgency: Urgency;
  reason?: string;
  toastVisible: boolean;
  popoverOpen: boolean;
  setPopoverOpen: (open: boolean) => void;
  closeToast: () => void;
  /** "Later" / "In 1 hour": snooze by time and close the popover. */
  snooze: () => void;
  refresh: () => void;
  /** Required dialog. */
  countdown: number;
  unsaved: boolean;
  saveError: boolean;
  saveAndRefresh: () => Promise<void>;
  discardAndRefresh: () => void;
}

const UpdateContext = createContext<UpdateContextValue | null>(null);

const INERT: UpdateContextValue = {
  urgency: 'none',
  toastVisible: false,
  popoverOpen: false,
  setPopoverOpen: () => undefined,
  closeToast: () => undefined,
  snooze: () => undefined,
  refresh: () => undefined,
  countdown: REQUIRED_COUNTDOWN_S,
  unsaved: false,
  saveError: false,
  saveAndRefresh: async () => undefined,
  discardAndRefresh: () => undefined,
};

/** Outside a provider (tests, isolated renders) the prompt is simply absent. */
export const useAppUpdate = (): UpdateContextValue => useContext(UpdateContext) ?? INERT;

export const UpdateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { remote } = useAppVersionCheck();
  const unsaved = useHasUnsavedChanges();
  const foreignModalOpen = useForeignModalOpen();

  const [serverRequired, setServerRequired] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [snoozedUntil, setSnoozedUntil] = useState<number | undefined>(readSnooze);
  const [toastVisible, setToastVisible] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [countdown, setCountdown] = useState(REQUIRED_COUNTDOWN_S);
  const [saveError, setSaveError] = useState(false);

  // First time this tab saw each remote build, for builds with no build time.
  const firstSeen = useRef<{ build: string; at: number }>({ build: '', at: 0 });
  const remoteBuild = remoteBuildOf(remote);
  if (remoteBuild && firstSeen.current.build !== remoteBuild) {
    firstSeen.current = { build: remoteBuild, at: Date.now() };
  }

  const baseUrgency: Urgency = serverRequired
    ? 'required'
    : deriveUrgency(remote as RemoteVersion | null, { hash: BUILD_HASH, timestamp: BUILD_TIMESTAMP });
  const availableSince = availableSinceOf(remote, firstSeen.current.at || now);
  const urgency = applyGrace(baseUrgency, availableSince, now);
  const reason = typeof remote?.reason === 'string' && remote.reason.trim() ? remote.reason.trim() : undefined;

  // Wake up exactly when a held-back `normal` update leaves its grace period.
  useEffect(() => {
    if (baseUrgency !== 'normal' || urgency !== 'none') return undefined;
    const wait = Math.max(0, availableSince + GRACE_MS - Date.now()) + 50;
    const id = window.setTimeout(() => setNow(Date.now()), wait);
    return () => window.clearTimeout(id);
  }, [baseUrgency, urgency, availableSince]);

  // The API said this build is below its floor.
  useEffect(() => onClientOutdated(() => setServerRequired(true)), []);

  // Coming back from a programmatic reload: go where the person was.
  useEffect(() => {
    const restore = takeRestoreState();
    const here = `${window.location.pathname}${window.location.search}`;
    if (restore && restore.path !== here) navigate(restore.path, { replace: true });
    // Boot only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pending = urgency === 'normal' || urgency === 'soon';

  // First notice: once per session, never mid-task, never while snoozed.
  useEffect(() => {
    if (!pending || toastVisible) return;
    if (toastAlreadyShown() || isSnoozed(snoozedUntil, Date.now())) return;
    // Read live: a screen registering in this same commit is not in `unsaved` yet.
    if (hasUnsavedChanges() || document.querySelector(FOREIGN_MODAL) || popoverOpen) return;
    markToastShown();
    setToastVisible(true);
  }, [pending, toastVisible, snoozedUntil, unsaved, foreignModalOpen, popoverOpen]);

  // A normal toast leaves by itself after 12s; a soon toast waits for the person.
  useEffect(() => {
    if (!toastVisible || urgency !== 'normal') return undefined;
    const id = window.setTimeout(() => setToastVisible(false), NORMAL_TOAST_MS);
    return () => window.clearTimeout(id);
  }, [toastVisible, urgency]);

  // Nothing pending (reloaded into the new build, or required took over).
  useEffect(() => {
    if (!pending) {
      setToastVisible(false);
      setPopoverOpen(false);
    }
  }, [pending]);

  // Route changes: close a normal toast, and apply a pending update with a full
  // load of the page just reached, but only when nothing is unsaved or open and
  // the navigation carries no in-memory router state that a load would drop.
  const lastPlace = useRef(`${location.pathname}${location.search}`);
  useEffect(() => {
    const place = `${location.pathname}${location.search}`;
    if (lastPlace.current === place) return; // hash-only or same-page navigation
    lastPlace.current = place;
    if (urgency === 'normal') setToastVisible(false);
    if (pending && !unsaved && !foreignModalOpen && location.state == null) {
      window.location.assign(`${place}${location.hash}`);
    }
  }, [location, urgency, pending, unsaved, foreignModalOpen]);

  // Required: count down, paused while there are unsaved edits.
  const required = urgency === 'required';
  useEffect(() => {
    if (!required || unsaved) return undefined;
    const id = window.setInterval(() => setCountdown((value) => value - 1), 1000);
    return () => window.clearInterval(id);
  }, [required, unsaved]);

  useEffect(() => {
    if (required && !unsaved && countdown <= 0) reloadApp();
  }, [required, unsaved, countdown]);

  const closeToast = useCallback(() => {
    setToastVisible(false);
    // A soon toast has no timeout, so closing it is a one-hour snooze.
    if (urgency === 'soon') {
      const until = Date.now() + snoozeDuration('soon');
      writeSnooze(until);
      setSnoozedUntil(until);
    }
  }, [urgency]);

  const snooze = useCallback(() => {
    const until = Date.now() + snoozeDuration(urgency);
    writeSnooze(until);
    setSnoozedUntil(until);
    setPopoverOpen(false);
  }, [urgency]);

  const saveAndRefresh = useCallback(async () => {
    setSaveError(false);
    try {
      await saveAllUnsaved();
    } catch {
      setSaveError(true);
      return;
    }
    reloadApp();
  }, []);

  const discardAndRefresh = useCallback(() => {
    discardAllUnsaved();
    reloadApp();
  }, []);

  const value = useMemo<UpdateContextValue>(
    () => ({
      urgency,
      reason,
      toastVisible: toastVisible && pending,
      popoverOpen: popoverOpen && pending,
      setPopoverOpen,
      closeToast,
      snooze,
      refresh: reloadApp,
      countdown: Math.max(0, countdown),
      unsaved,
      saveError,
      saveAndRefresh,
      discardAndRefresh,
    }),
    [
      urgency,
      reason,
      toastVisible,
      pending,
      popoverOpen,
      closeToast,
      snooze,
      countdown,
      unsaved,
      saveError,
      saveAndRefresh,
      discardAndRefresh,
    ],
  );

  return <UpdateContext.Provider value={value}>{children}</UpdateContext.Provider>;
};
